import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = ['Overview', 'Franchisee Agreements', 'Company Legal', 'Documents', 'Templates', 'Cases'];

function Legal({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [agreementStats, setAgreementStats] = useState(null);
  const [selectedAgreementId, setSelectedAgreementId] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchAgreementStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'Franchisee Agreements') {
      fetchAgreements();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch legal stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreementStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agreements/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreementStats(response.data);
    } catch (error) {
      console.error('Failed to fetch agreement stats:', error);
    }
  };

  const fetchAgreements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agreements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreements(response.data.agreements || []);
    } catch (error) {
      console.error('Failed to fetch agreements:', error);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Legal & Compliance</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <div className="flex gap-3">
              <button
                onClick={() => setActiveTab('Templates')}
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{backgroundColor: '#673ab7'}}
              >
                + New Template
              </button>
              <button
                onClick={() => setActiveTab('Documents')}
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{backgroundColor: '#2b3380'}}
              >
                + New Document
              </button>
              <button
                onClick={() => setActiveTab('Cases')}
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{backgroundColor: '#e91e63'}}
              >
                + New Case
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-x-auto">
          <div className="flex border-b min-w-max">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium transition ${
                  activeTab === tab
                    ? 'border-b-2 text-[#2b3380]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === tab ? {borderBottomColor: '#2b3380'} : {}}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'Overview' && !loading && stats && (
          <>
            {/* Legal Dashboard */}
            <MiniDashboard
              title="Legal & Compliance Overview"
              icon="⚖️"
              bgColor="#673ab7"
              stats={[
                {
                  label: 'Total Documents',
                  value: stats.total_documents || 0,
                  subtitle: 'All legal documents',
                  color: '#673ab7'
                },
                {
                  label: 'Pending Signatures',
                  value: stats.pending_signatures || 0,
                  subtitle: 'Awaiting signature',
                  color: '#ff9800'
                },
                {
                  label: 'Active Templates',
                  value: stats.total_templates || 0,
                  subtitle: 'Document templates',
                  color: '#2196f3'
                },
                {
                  label: 'Open Cases',
                  value: stats.open_cases || 0,
                  subtitle: 'Active legal cases',
                  color: '#e91e63'
                }
              ]}
            />

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Cases Overview</h3>
                  <span className="text-3xl">📋</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Cases</span>
                    <span className="font-bold text-gray-900">{stats.total_cases || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Open Cases</span>
                    <span className="font-bold text-orange-600">{stats.open_cases || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">High Priority</span>
                    <span className="font-bold text-red-600">{stats.high_priority_cases || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Resolved</span>
                    <span className="font-bold text-green-600">{stats.resolved_cases || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Document Status</h3>
                  <span className="text-3xl">📄</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Documents</span>
                    <span className="font-bold text-gray-900">{stats.total_documents || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Pending Signature</span>
                    <span className="font-bold text-orange-600">{stats.pending_signatures || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Signed</span>
                    <span className="font-bold text-green-600">{stats.signed_documents || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Documents by Category</h3>
                  <span className="text-3xl">📊</span>
                </div>
                <div className="space-y-3">
                  {stats.documents_by_category && Object.entries(stats.documents_by_category).map(([category, count]) => (
                    <div key={category} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{category}</span>
                      <span className="font-bold text-gray-900">{count}</span>
                    </div>
                  ))}
                  {(!stats.documents_by_category || Object.keys(stats.documents_by_category).length === 0) && (
                    <p className="text-sm text-gray-500 text-center py-4">No documents yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg p-6 mt-6 text-white">
              <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveTab('Documents')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-4 text-left transition"
                >
                  <div className="text-2xl mb-2">📝</div>
                  <div className="font-semibold">Generate Document</div>
                  <div className="text-sm opacity-90">Create from template</div>
                </button>
                <button
                  onClick={() => setActiveTab('Cases')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-4 text-left transition"
                >
                  <div className="text-2xl mb-2">⚠️</div>
                  <div className="font-semibold">Report Issue</div>
                  <div className="text-sm opacity-90">Create new case</div>
                </button>
                <button
                  onClick={() => setActiveTab('Templates')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-4 text-left transition"
                >
                  <div className="text-2xl mb-2">📋</div>
                  <div className="font-semibold">Manage Templates</div>
                  <div className="text-sm opacity-90">View all templates</div>
                </button>
              </div>
            </div>

            {/* Franchisee Agreements Stats (Added to Overview) */}
            {agreementStats && (
              <div className="mt-6">
                <h2 className="text-xl font-bold mb-4 text-gray-800">Franchisee Agreements Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Total Agreements</div>
                    <div className="text-3xl font-bold text-[#2b3380]">{agreementStats.total_agreements || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Active</div>
                    <div className="text-3xl font-bold text-green-600">{agreementStats.status_breakdown?.active || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Pending Signature</div>
                    <div className="text-3xl font-bold text-orange-600">{agreementStats.status_breakdown?.pending_signature || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="text-sm text-gray-600 mb-2">Draft</div>
                    <div className="text-3xl font-bold text-gray-600">{agreementStats.status_breakdown?.draft || 0}</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Franchisee Agreements Tab */}
        {activeTab === 'Franchisee Agreements' && (
          <FranchiseeAgreementsTab 
            agreements={agreements} 
            onRefresh={fetchAgreements}
            onView={(id) => setSelectedAgreementId(id)}
          />
        )}

        {/* Company Legal Tab */}
        {activeTab === 'Company Legal' && (
          <LegalRecordsPanel type="documents" title="Company Legal Documents" user={user} />
        )}

        {activeTab === 'Documents' && (
          <LegalRecordsPanel type="documents" title="Legal Documents" user={user} />
        )}

        {activeTab === 'Templates' && (
          <LegalRecordsPanel type="templates" title="Document Templates" user={user} />
        )}

        {activeTab === 'Cases' && (
          <LegalRecordsPanel type="cases" title="Legal Cases & Escalations" user={user} />
        )}

        {loading && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">Loading...</p>
          </div>
        )}

        {/* Agreement Detail Modal */}
        {selectedAgreementId && (
          <AgreementDetailModal
            agreementId={selectedAgreementId}
            onClose={() => setSelectedAgreementId(null)}
            onUpdate={() => {
              setSelectedAgreementId(null);
              fetchAgreements();
              fetchAgreementStats();
            }}
          />
        )}
      </div>
    </Layout>
  );
}

const legalRecordConfig = {
  documents: {
    nameKey: 'document_name', numberKey: 'document_number', endpoint: 'documents',
    empty: { document_name: '', category: 'Contract', party_name: '', status: 'Draft', content: '' },
    fields: [
      ['document_name', 'Document name', 'text', true], ['category', 'Category', 'select', true, ['Contract', 'NDA', 'Compliance', 'Notice', 'Other']],
      ['party_name', 'Party / entity', 'text'], ['status', 'Status', 'select', true, ['Draft', 'Pending Approval', 'Approved', 'Pending Signature', 'Signed']],
      ['content', 'Document content / notes', 'textarea', true],
    ],
  },
  templates: {
    nameKey: 'template_name', numberKey: 'category', endpoint: 'templates',
    empty: { template_name: '', category: 'Contract', description: '', template_content: '', is_active: true },
    fields: [
      ['template_name', 'Template name', 'text', true], ['category', 'Category', 'select', true, ['Contract', 'NDA', 'Compliance', 'Notice', 'Other']],
      ['description', 'Description', 'textarea'], ['template_content', 'Template content', 'textarea', true],
    ],
  },
  cases: {
    nameKey: 'case_title', numberKey: 'case_number', endpoint: 'cases',
    empty: { case_title: '', category: 'Compliance', priority: 'Medium', status: 'Open', description: '' },
    fields: [
      ['case_title', 'Case title', 'text', true], ['category', 'Category', 'select', true, ['Compliance', 'Contract', 'Dispute', 'Employment', 'Other']],
      ['priority', 'Priority', 'select', true, ['Low', 'Medium', 'High', 'Critical']], ['status', 'Status', 'select', true, ['Open', 'Investigation', 'In Progress', 'On Hold', 'Resolved']],
      ['description', 'Case description', 'textarea', true],
    ],
  },
};

function LegalRecordsPanel({ type, title, user }) {
  const config = legalRecordConfig[type];
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(config.empty);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken') || localStorage.getItem('token')}` });
  const loadRecords = async () => {
    try {
      setLoading(true); setMessage('');
      const response = await axios.get(`${API}/legal/${config.endpoint}`, { headers: headers() });
      setRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage(error.response?.data?.detail || `Unable to load ${title.toLowerCase()}.`);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadRecords(); }, [type]);

  const openCreate = () => { setEditing(null); setForm({ ...config.empty }); setShowForm(true); };
  const openEdit = (record) => {
    setEditing(record);
    setForm(config.fields.reduce((next, [key]) => ({ ...next, [key]: record[key] ?? config.empty[key] ?? '' }), {}));
    setShowForm(true);
  };
  const save = async (event) => {
    event.preventDefault();
    try {
      setMessage('');
      if (editing) await axios.put(`${API}/legal/${config.endpoint}/${editing.id}`, form, { headers: headers() });
      else await axios.post(`${API}/legal/${config.endpoint}`, form, { headers: headers() });
      setShowForm(false); setMessage(`${title} updated successfully.`); await loadRecords();
    } catch (error) { setMessage(error.response?.data?.detail || 'Unable to save this record.'); }
  };
  const remove = async (record) => {
    if (!window.confirm(`Delete ${record[config.nameKey] || 'this record'}?`)) return;
    try {
      await axios.delete(`${API}/legal/${config.endpoint}/${record.id}`, { headers: headers() });
      await loadRecords();
    } catch (error) { setMessage(error.response?.data?.detail || 'Unable to delete this record.'); }
  };
  const approve = async (record) => {
    try { await axios.post(`${API}/legal/documents/${record.id}/approve`, {}, { headers: headers() }); await loadRecords(); }
    catch (error) { setMessage(error.response?.data?.detail || 'Unable to approve this document.'); }
  };

  const filtered = records.filter((record) => JSON.stringify(record).toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-xl font-bold text-gray-900">{title}</h2><p className="text-sm text-gray-500">{records.length} database records</p></div>
        <button type="button" onClick={openCreate} className="px-4 py-2 rounded-lg bg-[#2b3380] text-white font-medium">+ Add {type === 'cases' ? 'Case' : type === 'templates' ? 'Template' : 'Document'}</button>
      </div>
      {message && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">{message}</div>}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${title.toLowerCase()}...`} className="w-full md:max-w-md border rounded-lg px-3 py-2" /></div>
        {loading ? <div className="p-10 text-center text-gray-500">Loading {title.toLowerCase()}...</div> : filtered.length === 0 ? (
          <div className="p-10 text-center"><div className="text-4xl mb-3">⚖️</div><h3 className="font-semibold">No records found</h3><p className="text-sm text-gray-500 mt-1">Create the first record to activate this workflow.</p></div>
        ) : <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200"><thead className="bg-gray-50"><tr><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Name</th><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Reference</th><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Status</th><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Updated</th><th className="px-5 py-3 text-right text-xs uppercase text-gray-500">Actions</th></tr></thead><tbody className="divide-y divide-gray-100">{filtered.map((record) => <tr key={record.id}><td className="px-5 py-4 font-medium text-gray-900">{record[config.nameKey] || record.title || 'Untitled'}</td><td className="px-5 py-4 text-sm text-gray-600">{record[config.numberKey] || record.category || '—'}</td><td className="px-5 py-4"><span className="px-2 py-1 rounded-full bg-gray-100 text-xs font-medium">{record.status || (record.is_active === false ? 'Inactive' : 'Active')}</span></td><td className="px-5 py-4 text-sm text-gray-500">{record.updated_at ? formatDate(record.updated_at) : '—'}</td><td className="px-5 py-4"><div className="flex justify-end gap-2">{type === 'documents' && record.status === 'Pending Approval' && user?.role === 'Admin' && <button onClick={() => approve(record)} className="text-green-700 text-sm font-medium">Approve</button>}<button onClick={() => openEdit(record)} className="text-[#2b3380] text-sm font-medium">Edit</button>{user?.role === 'Admin' && <button onClick={() => remove(record)} className="text-red-600 text-sm font-medium">Delete</button>}</div></td></tr>)}</tbody></table></div>}
      </div>
      {showForm && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"><div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"><div className="p-5 border-b flex justify-between"><h3 className="text-xl font-bold">{editing ? 'Edit' : 'Create'} {title}</h3><button onClick={() => setShowForm(false)} aria-label="Close">✕</button></div><form onSubmit={save} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">{config.fields.map(([key, label, kind, required, options]) => <label key={key} className={kind === 'textarea' ? 'md:col-span-2' : ''}><span className="block text-sm font-medium mb-1">{label}{required ? ' *' : ''}</span>{kind === 'select' ? <select value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} required={required} className="w-full border rounded-lg px-3 py-2">{options.map((option) => <option key={option}>{option}</option>)}</select> : kind === 'textarea' ? <textarea value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} required={required} rows="5" className="w-full border rounded-lg px-3 py-2" /> : <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} required={required} className="w-full border rounded-lg px-3 py-2" />}</label>)}<div className="md:col-span-2 flex justify-end gap-3 pt-2"><button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg">Cancel</button><button type="submit" className="px-4 py-2 bg-[#2b3380] text-white rounded-lg">Save</button></div></form></div></div>}
    </div>
  );
}

// ==================== FRANCHISEE AGREEMENTS TAB ====================
function FranchiseeAgreementsTab({ agreements, onRefresh, onView }) {
  const getStatusBadge = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Signature': 'bg-yellow-100 text-yellow-800',
      'Signed': 'bg-[#2b338030] text-[#2b3380]',
      'Active': 'bg-green-100 text-green-800',
      'Expired': 'bg-red-100 text-red-800',
      'Terminated': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">All Franchisee Agreements</h3>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agreement #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Franchise Fee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {agreements.map((agreement) => (
              <tr key={agreement.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-[#2b3380]">{agreement.agreement_number}</td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{agreement.store_name}</div>
                  <div className="text-sm text-gray-500">{agreement.store_code}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{agreement.applicant_name}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{agreement.store_type}</td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right">₹{agreement.franchise_fee?.toLocaleString()}</td>
                <td className="px-6 py-4">{getStatusBadge(agreement.status)}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => onView(agreement.id)}
                    className="text-[#2b3380] hover:text-blue-900 text-sm font-medium"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {agreements.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No agreements found. Create agreements from approved leads!
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== AGREEMENT DETAIL MODAL ====================
function AgreementDetailModal({ agreementId, onClose, onUpdate }) {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchDetails();
  }, [agreementId]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agreements/${agreementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreement(response.data.agreement);
      setFees(response.data.fees || []);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching agreement details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (!agreement) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{agreement.agreement_number}</h2>
            <p className="text-gray-600">{agreement.store_name} • {agreement.applicant_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Agreement Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Store Type:</span>
                <span className="ml-2 font-medium">{agreement.store_type}</span>
              </div>
              <div>
                <span className="text-gray-600">Model:</span>
                <span className="ml-2 font-medium">{agreement.ownership_model}</span>
              </div>
              <div>
                <span className="text-gray-600">Territory:</span>
                <span className="ml-2 font-medium">{agreement.territory}</span>
              </div>
              <div>
                <span className="text-gray-600">Duration:</span>
                <span className="ml-2 font-medium">{agreement.duration_months} months</span>
              </div>
            </div>
          </div>

          {/* Financial Terms */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Financial Terms</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Franchise Fee:</span>
                <span className="ml-2 font-medium text-lg">₹{agreement.franchise_fee?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Security Deposit:</span>
                <span className="ml-2 font-medium text-lg">₹{agreement.security_deposit?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Royalty:</span>
                <span className="ml-2 font-medium">{agreement.royalty_percentage}%</span>
              </div>
              <div>
                <span className="text-gray-600">Marketing Fee:</span>
                <span className="ml-2 font-medium">{agreement.marketing_fee_percentage}%</span>
              </div>
            </div>
          </div>

          {/* Signature Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3">Signature Status</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span>Franchisee:</span>
                <span className={agreement.franchisee_signed ? 'text-green-600 font-medium' : 'text-gray-500'}>
                  {agreement.franchisee_signed ? '✓ Signed' : '✗ Pending'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Company:</span>
                <span className={agreement.company_signed ? 'text-green-600 font-medium' : 'text-gray-500'}>
                  {agreement.company_signed ? '✓ Signed' : '✗ Pending'}
                </span>
              </div>
            </div>
          </div>

          {/* Fee Structure */}
          {fees.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Fee Structure</h3>
              <div className="space-y-2">
                {fees.map(fee => (
                  <div key={fee.id} className="bg-white border rounded p-3 text-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{fee.fee_name}</div>
                        <div className="text-gray-500 text-xs">Due: {new Date(fee.due_date).toLocaleDateString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">₹{fee.total_amount?.toLocaleString()}</div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          fee.payment_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {fee.payment_status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment History */}
          {transactions.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Payment History</h3>
              <div className="space-y-2">
                {transactions.map(txn => (
                  <div key={txn.id} className="bg-white border rounded p-3 text-sm">
                    <div className="flex justify-between">
                      <div>
                        <div className="font-medium">{txn.transaction_number}</div>
                        <div className="text-gray-500 text-xs">{txn.payment_method} • {new Date(txn.transaction_date).toLocaleDateString()}</div>
                      </div>
                      <div className="font-bold text-green-600">₹{txn.amount?.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default Legal;
